/**
 * Composable, not a Pinia store, on purpose:
 *
 * The three upload flows (CT, hip-spine, xray) share the upload *contract* —
 * state machine, validation orchestration, worker coordination — but each flow
 * must hold its own independent state. A defineStore('upload-lifecycle', ...)
 * would be a singleton with cross-flow state bleed (uploading CT then switching
 * tabs to xray would inherit CT's state). Instantiating one composable per
 * per-flow ViewModel gives three independent state machines.
 *
 * The state-machine transitions and derived flags live in dicomUploadActions.ts
 * and dicomUploadFlags.ts; they operate on the shared {@link DicomUploadContext}
 * this composable builds, keeping the setup body a thin composition root. See
 * docs/architecture.md → "Decomposing a large setup store or ViewModel".
 */

import { computed, ref, type Ref } from 'vue';
import type { ProcessDicomsResult } from '@/lib/dicom/validateDicoms';
import type { DicomInfo } from '@/lib/dicom/DicomInfo';
import type { DicomGroups } from '@/lib/dicom/DicomSeries';
import type { UploadState } from '@/views/case-settings/webworkers/types';
import { makeInitialUploadState } from '@/views/case-settings/webworkers/uploadWorkerUtils';
import { useCaseSettings } from '@/stores/caseSettings/store';
import type { DicomUploadLifecycleConfig, WorkerFile } from '@/views/case-settings/upload/shared/upload.types';
import { createDicomUploadFlags } from '@/views/case-settings/upload/shared/dicomUploadFlags';
import { createDicomUploadActions } from '@/views/case-settings/upload/shared/dicomUploadActions';

/**
 * The mutable handles the extracted flags/actions operate on: the per-flow
 * reactive refs plus the flow `config` and the case-settings store. Bundling
 * them lets the transitions live in dicomUploadActions.ts while this composable
 * stays a thin composition root.
 */
export type DicomUploadContext<TInput, TPayload = WorkerFile> = {
    state: Ref<UploadState>;
    processDicomsResult: Ref<ProcessDicomsResult | null>;
    validatedDicoms: Ref<DicomInfo[]>;
    inputs: { value: TInput[] };
    seriesGroups: Ref<DicomGroups>;
    caseSettings: ReturnType<typeof useCaseSettings>;
    config: DicomUploadLifecycleConfig<TInput, TPayload>;
};

export function useDicomUploadLifecycle<TInput, TPayload = WorkerFile>(
    config: DicomUploadLifecycleConfig<TInput, TPayload>,
) {
    const ctx: DicomUploadContext<TInput, TPayload> = {
        state: ref(makeInitialUploadState()),
        processDicomsResult: ref<ProcessDicomsResult | null>(null),
        validatedDicoms: ref<DicomInfo[]>([]),
        inputs: ref<TInput[]>([]) as { value: TInput[] },
        seriesGroups: ref<DicomGroups>({}),
        caseSettings: useCaseSettings(),
        config,
    };

    return {
        state: computed(() => ctx.state.value),
        error: computed(() => ctx.state.value.error),
        hasUploaded: computed(() => ctx.state.value.hasUploaded),
        progress: computed(() => ctx.state.value.progress),
        patientIdentityCheck: computed(() => ctx.state.value.patientIdentityCheck),
        patientComparison: computed(() => ctx.state.value.patientComparison),
        processDicomsResult: computed(() => ctx.processDicomsResult.value),
        inputs: computed(() => ctx.inputs.value),
        seriesGroups: computed(() => ctx.seriesGroups.value),
        ...createDicomUploadFlags(ctx),
        ...createDicomUploadActions(ctx),
    };
}